import { useEffect, useState } from "react";
import { Tv, Loader2, Hammer, Save, Download, History, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChannelForm } from "@/components/ChannelForm";
import { RokuPreview, LeanbackPreview, TvFrame } from "@/components/TvPreview";
import { StoreListing, listingTemplate } from "@/components/StoreListing";
import { ChannelSettings } from "@/components/ChannelSettings";
import { InstallQr } from "@/components/InstallQr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ApkCell({ b, onInstall }) {
  const link = "h-8 px-3 rounded-lg border border-emerald-500/30 text-xs text-emerald-300 hover:border-emerald-500/60 inline-flex items-center gap-1.5 transition-colors";
  const run = b.apk_run_url && <a href={b.apk_run_url} target="_blank" rel="noreferrer" className="text-[11px] text-gray-400 hover:text-white underline">view on GitHub</a>;
  if (b.apk_status === "ready") return (
    <>
      <a data-testid={`build-download-apk-${b.id}`} href={`${API}/channel-builder/builds/${b.id}/apk`} className={link}><Download className="h-3.5 w-3.5" /> APK · {(b.apk_size_kb / 1024).toFixed(1)} MB</a>
      {b.aab_size_kb != null && <a data-testid={`build-download-aab-${b.id}`} href={`${API}/channel-builder/builds/${b.id}/aab`} className={link}><Download className="h-3.5 w-3.5" /> Play bundle · {(b.aab_size_kb / 1024).toFixed(1)} MB</a>}
      <InstallQr build={b} onChange={(code) => onInstall(b.id, code)} />
    </>
  );
  if (b.apk_status === "queued" || b.apk_status === "building") return (
    <span data-testid={`build-apk-status-${b.id}`} className="text-xs text-amber-300 inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {b.apk_status === "queued" ? "Waiting for GitHub…" : "Building signed APK…"} {run}</span>
  );
  if (b.apk_status === "failed") return <span data-testid={`build-apk-status-${b.id}`} className="text-xs text-red-400 inline-flex items-center gap-1.5 max-w-xs">APK failed: {b.apk_error} {run}</span>;
  return null;
}

function BuildHistory({ builds, setBuilds }) {
  const onInstall = (id, code) => setBuilds((bs) => bs.map((x) => (x.id === id ? { ...x, install_code: code } : x)));
  return (
    <div className="rounded-2xl border border-gray-800 bg-card p-5" data-testid="build-history">
      <p className="font-display font-semibold text-white flex items-center gap-2 mb-3"><History className="h-4 w-4 text-amber-400" /> Build history <span className="text-xs font-normal text-gray-500">last 5 builds</span></p>
      {builds.length === 0 ? <p data-testid="build-history-empty" className="text-sm text-gray-500">No builds yet. Fill in the details and press Build packages.</p> : (
        <div className="divide-y divide-gray-800">
          {builds.map((b) => (
            <div key={b.id} data-testid={`build-row-${b.id}`} className="py-3 flex flex-wrap items-center gap-3">
              <span className="font-mono text-xs rounded bg-amber-500/10 text-amber-300 px-2 py-0.5">v{b.version}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white truncate">{b.church_name}</p>
                <p className="text-xs text-gray-500">{new Date(b.created_at).toLocaleString()}</p>
              </div>
              <a data-testid={`build-download-roku-${b.id}`} href={`${API}/channel-builder/builds/${b.id}/roku`} className="h-8 px-3 rounded-lg border border-gray-800 text-xs text-gray-300 hover:text-amber-400 hover:border-amber-500/40 inline-flex items-center gap-1.5 transition-colors">
                <Download className="h-3.5 w-3.5" /> Roku · {b.roku_size_kb} KB
              </a>
              <ApkCell b={b} onInstall={onInstall} />
              <a data-testid={`build-download-android-${b.id}`} href={`${API}/channel-builder/builds/${b.id}/android`} className="h-8 px-3 rounded-lg border border-gray-800 text-xs text-gray-300 hover:text-amber-400 hover:border-amber-500/40 inline-flex items-center gap-1.5 transition-colors">
                <Download className="h-3.5 w-3.5" /> Android TV · {b.android_size_kb} KB
              </a>
              {b.artwork_size_kb != null && (
                <a data-testid={`build-download-artwork-${b.id}`} href={`${API}/channel-builder/builds/${b.id}/artwork`} className="h-8 px-3 rounded-lg border border-amber-500/30 text-xs text-amber-300 hover:text-amber-200 hover:border-amber-500/60 inline-flex items-center gap-1.5 transition-colors">
                  <ImageIcon className="h-3.5 w-3.5" /> Store artwork · {b.artwork_size_kb >= 1024 ? `${(b.artwork_size_kb / 1024).toFixed(1)} MB` : `${b.artwork_size_kb} KB`}
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ChannelBuilder() {
  const [churches, setChurches] = useState([]);
  const [owner, setOwner] = useState("");
  const [pkg, setPkg] = useState(null);
  const [builds, setBuilds] = useState([]);
  const [busy, setBusy] = useState("");
  const [facts, setFacts] = useState({});

  useEffect(() => { api.get("/channel-builder/churches").then((r) => setChurches(r.data)).catch(() => toast.error("Couldn't load churches")); }, []);
  useEffect(() => {
    if (!owner) return;
    setPkg(null);
    api.get(`/channel-builder/churches/${owner}`).then((r) => {
      setFacts(r.data.facts);
      setPkg({ ...r.data.package, listing: r.data.package.listing || listingTemplate(r.data.package.church_name, r.data.facts) });
      setBuilds(r.data.builds);
    })
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load this church"));
  }, [owner]);

  const set = (k, v) => setPkg((p) => ({ ...p, [k]: v }));
  const pending = builds.some((b) => ["queued", "building"].includes(b.apk_status));
  useEffect(() => {
    if (!owner || !pending) return undefined;
    const t = setInterval(() => api.get(`/channel-builder/churches/${owner}`).then((r) => setBuilds(r.data.builds)).catch(() => {}), 15000);
    return () => clearInterval(t);
  }, [owner, pending]);
  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "save") { await api.put(`/channel-builder/churches/${owner}`, pkg); toast.success("Saved"); }
      else {
        const r = await api.post(`/channel-builder/churches/${owner}/build`, pkg);
        setBuilds((b) => [r.data, ...b].slice(0, 5));
        toast.success(r.data.apk_status === "queued" ? `Build v${r.data.version} is ready. GitHub is now building the signed APK (about 5–10 minutes).` : `Build v${r.data.version} is ready to download`);
      }
      setChurches((cs) => cs.map((c) => (c.user_id === owner ? { ...c, has_package: true, name: pkg.church_name } : c)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="channel-builder-page">
      <div>
        <h1 className="font-display text-3xl font-bold text-white flex items-center gap-3"><Tv className="h-7 w-7 text-amber-400" /> Channel Package Builder</h1>
        <p className="text-sm text-gray-400 mt-1">Make a branded Roku channel and an Android TV / Google TV / Fire TV app for any church.</p>
      </div>
      <ChannelSettings />
      <div className="max-w-md">
        <Select value={owner} onValueChange={setOwner}>
          <SelectTrigger data-testid="channel-church-select" className="bg-card border-gray-800"><SelectValue placeholder="Choose a church…" /></SelectTrigger>
          <SelectContent className="bg-card border-gray-800 max-h-80">
            {churches.map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.name}{c.has_package ? " ✓" : ""}<span className="text-gray-500"> · {c.email}</span></SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {owner && !pkg && <div className="flex items-center gap-2 text-gray-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
      {pkg && (
        <>
          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-6 items-start">
            <div className="rounded-2xl border border-gray-800 bg-card p-5 space-y-5">
              <ChannelForm pkg={pkg} set={set} />
              <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-800">
                <button data-testid="channel-build-button" onClick={() => run("build")} disabled={!!busy}
                  className="h-10 px-4 rounded-lg gold-gradient text-black text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
                  {busy === "build" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Hammer className="h-4 w-4" />} Generate
                </button>
                <button data-testid="channel-save-button" onClick={() => run("save")} disabled={!!busy}
                  className="h-10 px-4 rounded-lg border border-gray-800 text-sm text-gray-300 hover:text-white inline-flex items-center gap-2 disabled:opacity-60">
                  {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save details
                </button>
              </div>
            </div>
            <div className="space-y-6 lg:sticky lg:top-6" data-testid="channel-preview">
              <TvFrame label="Roku"><RokuPreview pkg={pkg} /></TvFrame>
              <TvFrame label="Google TV / Fire TV"><LeanbackPreview pkg={pkg} /></TvFrame>
            </div>
          </div>
          <StoreListing listing={pkg.listing} setListing={(l) => set("listing", l)} saving={busy === "save"}
            onReset={() => set("listing", listingTemplate(pkg.church_name, facts))} onSave={() => run("save")} />
          <BuildHistory builds={builds} setBuilds={setBuilds} />
        </>
      )}
    </div>
  );
}
